import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import { useState, useCallback, useMemo } from 'react';
import '../utils/assertion.esm.js';
import '../utils/ssr.esm.js';
import '../utils/number.esm.js';
import '../utils/array.esm.js';
import '../utils/breakpoint.esm.js';
import '../utils/context.esm.js';
import '../utils/dom.esm.js';
import '../utils/tabbable.esm.js';
import '../utils/dom-query.esm.js';
import '../utils/logger.esm.js';
import '../utils/focus.esm.js';
import { noop } from '../utils/function.esm.js';
import '../utils/keydown.esm.js';
import '../utils/lazy.esm.js';
import '../utils/object.esm.js';
import '../utils/react-helpers.esm.js';
import '../utils/style-props.esm.js';
import '../utils/tailwind.esm.js';
import { useSafeLayoutEffect } from './use-safe-layout-effects.esm.js';
import { useForceUpdate } from './use-force-update.esm.js';

function useDescendant(props) {
  var context = props.context,
      element = props.element,
      indexProp = props.index,
      disabled = props.disabled,
      focusable = props.focusable,
      rest = _objectWithoutPropertiesLoose(props, ["context", "element", "index", "disabled", "focusable"]);

  var forceUpdate = useForceUpdate();
  var register = context.register,
      unregister = context.unregister,
      descendants = context.descendants;
  useSafeLayoutEffect(function () {
    if (!element) {
      forceUpdate();
    } //  Don't register this descendant if it's disabled and not focusable


    if (disabled && !focusable) {
      return noop;
    } //  else, register the descendant
    // eslint-disable-next-line @typescript-eslint/no-explicit-any


    register(_extends({
      element: element,
      disabled: disabled,
      focusable: focusable
    }, rest)); // when it unmounts, unregister the descendant

    return function () {
      if (element) {
        unregister(element);
      }
    }; // eslint-disable-next-line
  }, [element].concat(Object.values(rest)));
  return indexProp != null ? indexProp : descendants.findIndex(function (descendant) {
    return descendant.element === element;
  });
}
function useDescendants() {
  var _React$useState = useState([]),
      descendants = _React$useState[0],
      setDescendants = _React$useState[1];

  var register = useCallback(function (_ref) {
    var element = _ref.element,
        rest = _objectWithoutPropertiesLoose(_ref, ["element"]);

    if (!element) return; // @ts-ignore

    setDescendants(function (prevDescendants) {
      if (prevDescendants.find(function (item) {
        return item.element === element;
      }) == null) {
        var index = prevDescendants.findIndex(function (item) {
          if (!item.element || !element) return false; // eslint-disable-next-line no-bitwise

          return Boolean(item.element.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_PRECEDING);
        });

        var newItem = _extends({
          element: element
        }, rest);

        if (index === -1) {
          return [].concat(prevDescendants, [newItem]);
        }

        return [].concat(prevDescendants.slice(0, index), [newItem], prevDescendants.slice(index));
      }

      return prevDescendants;
    });
  }, []);
  var unregister = useCallback(function (element) {
    if (!element) {
      return;
    }

    setDescendants(function (d) {
      return d.filter(function (descendant) {
        return element !== descendant.element;
      });
    });
  }, []);
  var context = useMemo(function () {
    return {
      descendants: descendants,
      register: register,
      unregister: unregister
    };
  }, [descendants, register, unregister]);
  return context;
}

export { useDescendant, useDescendants };
//# sourceMappingURL=use-descendant.esm.js.map
